/**
 * HUD - Heads-Up Display Overlay
 * 
 * HTML-based UI overlay for game information.
 * Uses dirty checking to minimize DOM updates.
 */

import { GameStateInfo } from '../game/Game';

/** HUD configuration */
export interface HUDConfig {
    /** Whether to show FPS counter */
    showFps?: boolean;
    /** Whether to show debug info */
    debug?: boolean;
}

/**
 * HUD class - manages the in-game overlay UI
 */
export class HUD {
    private container: HTMLDivElement;
    private config: HUDConfig;
    
    // Element references
    private scoreEl!: HTMLElement;
    private densityEl!: HTMLElement;
    private tJunctionsEl!: HTMLElement;
    private sortLeftEl!: HTMLElement;
    private timerEl!: HTMLElement;
    private fpsEl!: HTMLElement;
    
    // Dirty checking state
    private lastState: Partial<GameStateInfo> = {};
    private lastSortLeftColor = '';
    private lastTimerColor = '';

    constructor(parentElement: HTMLElement, config: HUDConfig = {}) {
        this.config = {
            showFps: config.showFps ?? false,
            debug: config.debug ?? false,
        };

        // Create container
        this.container = document.createElement('div');
        this.container.id = 'game-hud';
        this.container.innerHTML = this.getHTML();
        parentElement.appendChild(this.container);

        // Apply styles
        this.applyStyles();

        // Cache element references
        this.cacheElements();
    }

    /**
     * Get the HUD HTML structure
     */
    private getHTML(): string {
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        const controlsText = isTouchDevice 
            ? 'Drag Idol | Arc Motion to Pose' 
            : 'Drag Idol | A/D to Pose';

        return `
            <div class="hud-score-panel">
                <div class="hud-score">
                    <span class="hud-score-value">0</span>
                </div>
                <div class="hud-details">
                    <span class="hud-density">Charm: 0%</span>
                    <span class="hud-separator">•</span>
                    <span class="hud-tjunctions">Idol Links: 0</span>
                </div>
                <div class="hud-sortleft">Encore Left: 100%</div>
            </div>
            <div class="hud-timer-panel">
                <div class="hud-timer">∞</div>
                <div class="hud-timer-label">SHOWTIME</div>
            </div>
            <div class="hud-controls">${controlsText}</div>
            ${this.config.showFps ? '<div class="hud-fps">FPS: 60</div>' : ''}
        `;
    }

    /**
     * Apply CSS styles
     */
    private applyStyles(): void {
        const style = document.createElement('style');
        style.textContent = `
            #game-hud {
                --hud-ink: #eef3ff;
                --hud-soft: rgba(238, 243, 255, 0.72);
                --hud-panel: rgba(11, 15, 33, 0.58);
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                pointer-events: none;
                font-family: 'Trebuchet MS', 'Segoe UI', sans-serif;
                z-index: 100;
            }

            #game-hud * {
                pointer-events: none;
            }

            .hud-score-panel {
                position: absolute;
                top: 15px;
                left: 50%;
                transform: translateX(-50%);
                text-align: center;
                color: var(--hud-ink);
                background: var(--hud-panel);
                border: 1px solid rgba(255,255,255,0.12);
                border-radius: 16px;
                padding: 10px 18px;
                box-shadow: 0 10px 35px rgba(0,0,0,0.24);
            }

            .hud-score {
                font-size: 32px;
                font-weight: bold;
                text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
            }

            .hud-score-value {
                color: #fff6bf;
                text-shadow: 0 0 18px rgba(255, 230, 109, 0.35);
            }

            .hud-details {
                font-size: 14px;
                color: var(--hud-soft);
                margin-top: 5px;
            }

            .hud-separator {
                margin: 0 8px;
                color: #666;
            }

            .hud-tjunctions {
                color: #9dd8ff;
            }

            .hud-sortleft {
                font-size: 16px;
                font-weight: 600;
                margin-top: 8px;
                color: #6cf0b6;
                transition: color 0.3s ease;
            }

            .hud-timer-panel {
                position: absolute;
                top: 120px;
                left: 50%;
                transform: translateX(-50%);
                text-align: center;
                background: var(--hud-panel);
                border: 1px solid rgba(255,255,255,0.1);
                border-radius: 14px;
                padding: 10px 16px;
                box-shadow: 0 10px 30px rgba(0,0,0,0.22);
            }

            .hud-timer {
                font-size: 42px;
                font-weight: bold;
                color: #c6cce8;
                text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
                transition: color 0.3s ease;
            }

            .hud-timer.warning {
                color: #ff7ab8;
                animation: pulse 0.5s ease-in-out infinite;
            }

            @keyframes pulse {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.1); }
            }

            .hud-timer-label {
                font-size: 12px;
                color: var(--hud-soft);
                text-transform: uppercase;
                letter-spacing: 2px;
            }

            .hud-controls {
                position: absolute;
                bottom: 15px;
                left: 50%;
                transform: translateX(-50%);
                font-size: 14px;
                color: var(--hud-soft);
                background: rgba(10, 14, 31, 0.55);
                border: 1px solid rgba(255,255,255,0.08);
                border-radius: 999px;
                padding: 8px 14px;
            }

            .hud-fps {
                position: absolute;
                top: 10px;
                right: 15px;
                font-size: 12px;
                font-family: monospace;
                color: #0f0;
            }

            /* Phase-specific styles */
            .hud-finishing .hud-timer {
                color: #ffe66d;
            }

            .hud-settling .hud-score-panel,
            .hud-settling .hud-timer-panel {
                opacity: 0.5;
            }
        `;
        document.head.appendChild(style);
    }

    /**
     * Cache element references for fast updates
     */
    private cacheElements(): void {
        this.scoreEl = this.container.querySelector('.hud-score-value')!;
        this.densityEl = this.container.querySelector('.hud-density')!;
        this.tJunctionsEl = this.container.querySelector('.hud-tjunctions')!;
        this.sortLeftEl = this.container.querySelector('.hud-sortleft')!;
        this.timerEl = this.container.querySelector('.hud-timer')!;
        
        const fpsEl = this.container.querySelector('.hud-fps');
        if (fpsEl) {
            this.fpsEl = fpsEl as HTMLElement;
        }
    }

    /**
     * Update the HUD with current game state.
     * Uses dirty checking to minimize DOM updates.
     */
    update(state: GameStateInfo): void {
        // Score
        const score = Math.floor(state.score);
        if (score !== this.lastState.score) {
            this.scoreEl.textContent = score.toLocaleString();
            this.lastState.score = score;
        }

        // Density
        const density = Math.floor(state.density);
        if (density !== this.lastState.density) {
            this.densityEl.textContent = `Charm: ${density}%`;
            this.lastState.density = density;
        }

        // T-Junctions
        if (state.tJunctionCount !== this.lastState.tJunctionCount) {
            this.tJunctionsEl.textContent = `Idol Links: ${state.tJunctionCount}`;
            this.lastState.tJunctionCount = state.tJunctionCount;
        }

        // Sort Left
        if (state.sortLeftPercent !== this.lastState.sortLeftPercent) {
            this.sortLeftEl.textContent = `Encore Left: ${state.sortLeftPercent}%`;
            this.lastState.sortLeftPercent = state.sortLeftPercent;
            
            // Update color
            let color = '#6cf0b6'; // Green
            if (state.sortLeftPercent <= 0) {
                color = '#ff7ab8'; // Pink alert
            } else if (state.sortLeftPercent <= 20) {
                color = '#ffe66d'; // Yellow
            }
            if (color !== this.lastSortLeftColor) {
                this.sortLeftEl.style.color = color;
                this.lastSortLeftColor = color;
            }
        }

        // Timer
        if (state.timeLeft !== this.lastState.timeLeft || state.isFinishing !== this.lastState.isFinishing) {
            if (state.timeLeft !== null && state.isFinishing) {
                this.timerEl.textContent = state.timeLeft.toString();
                
                // Warning state for final 5 seconds
                const isWarning = state.timeLeft <= 5;
                this.timerEl.classList.toggle('warning', isWarning);
                
                if (isWarning && this.lastTimerColor !== 'warning') {
                    this.timerEl.style.color = '';  // Let CSS handle it
                    this.lastTimerColor = 'warning';
                } else if (!isWarning && this.lastTimerColor !== 'finishing') {
                    this.timerEl.style.color = '#ffe66d';
                    this.lastTimerColor = 'finishing';
                }
            } else {
                this.timerEl.textContent = '∞';
                this.timerEl.classList.remove('warning');
                if (this.lastTimerColor !== 'normal') {
                    this.timerEl.style.color = '#c6cce8';
                    this.lastTimerColor = 'normal';
                }
            }
            this.lastState.timeLeft = state.timeLeft;
            this.lastState.isFinishing = state.isFinishing;
        }

        // FPS
        if (this.fpsEl && state.fps !== this.lastState.fps) {
            this.fpsEl.textContent = `FPS: ${state.fps}`;
            
            // Color based on performance
            let fpsColor = '#0f0';  // Green
            if (state.fps < 30) {
                fpsColor = '#f44336';  // Red
            } else if (state.fps < 55) {
                fpsColor = '#ffeb3b';  // Yellow
            }
            this.fpsEl.style.color = fpsColor;
            this.lastState.fps = state.fps;
        }

        // Phase class
        if (state.phase !== this.lastState.phase) {
            this.container.classList.remove('hud-menu', 'hud-playing', 'hud-settling', 'hud-gameover');
            this.container.classList.add(`hud-${state.phase}`);
            
            // Add finishing class
            this.container.classList.toggle('hud-finishing', state.isFinishing);
            
            this.lastState.phase = state.phase;
        }
    }

    /**
     * Show the HUD
     */
    show(): void {
        this.container.style.display = 'block';
    }

    /**
     * Hide the HUD
     */
    hide(): void {
        this.container.style.display = 'none';
    }

    /**
     * Clean up
     */
    destroy(): void {
        this.container.remove();
    }
}
